import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Linkedin, ArrowRight } from "lucide-react";
import { useLang } from "@/App";
import { Reveal, ChapterHead } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG_CONTACT = "https://prieure-saint-leonard.fr/api/files/prieure-saint-leonard/uploads/2702878b-0556-4d7d-93b6-80052672444d.jpg";

const Contact = ({ presetSubject }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (presetSubject) setForm((f) => ({ ...f, subject: presetSubject }));
  }, [presetSubject]);

  useEffect(() => {
    setForm({ name: "", email: "", subject: "", message: "" });
  }, [lang]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("fail");
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-line focus:border-terra outline-none py-3 text-ink placeholder:text-muted2/50 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative px-5 md:px-10 py-28 md:py-40 overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 -z-10">
        <img src={IMG_CONTACT} alt="" className="w-full h-full object-cover img-lumiere opacity-[0.16]" />
        <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/90 to-paper/60" />
      </div>

      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <ChapterHead kicker={t.contact.kicker} title={t.contact.title} testId="contact" />
          <Reveal delay={0.1}>
            <p className="text-ink2 text-base leading-relaxed max-w-md -mt-6 md:-mt-10 mb-12">{t.contact.intro}</p>
            <div className="space-y-6" data-testid="contact-coordinates">
              <div className="flex gap-4 items-start">
                <MapPin size={18} className="text-terra mt-1 shrink-0" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze mb-1">{t.contact.coordsTitle}</p>
                  <p className="text-sm text-ink2 leading-relaxed max-w-xs">{t.contact.address}</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Phone size={18} className="text-terra mt-1 shrink-0" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze mb-1">{t.contact.phoneLabel}</p>
                  <a data-testid="contact-phone-link" href={`tel:${t.contact.phone.replace(/\s/g, "")}`} className="text-sm text-ink2 hover:text-terra transition-colors">{t.contact.phone}</a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Mail size={18} className="text-terra mt-1 shrink-0" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze mb-1">{t.contact.emailLabel}</p>
                  <a data-testid="contact-email-link" href={`mailto:${t.contact.emailAddr}`} className="text-sm text-ink2 hover:text-terra transition-colors">{t.contact.emailAddr}</a>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Linkedin size={18} className="text-terra mt-1 shrink-0" />
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze mb-1">LinkedIn</p>
                  <a data-testid="contact-linkedin-link" href="https://www.linkedin.com/in/gdechassey" target="_blank" rel="noreferrer" className="text-sm text-ink2 hover:text-terra transition-colors link-underline">
                    linkedin.com/in/gdechassey
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>


        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form data-testid="contact-form-container" onSubmit={submit} className="bg-paper/80 backdrop-blur-sm border border-line p-7 md:p-12 space-y-8 shadow-[0_30px_80px_-40px_rgba(33,26,19,0.35)]">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label htmlFor="contact-name" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze block mb-2">{t.contact.name}</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} type="text" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze block mb-2">{t.contact.email}</label>
                  <input id="contact-email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className={inputCls} type="email" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze block mb-2">{t.contact.subject}</label>
                <select id="contact-subject" data-testid="contact-subject-select" required value={form.subject} onChange={set("subject")} className={`${inputCls} cursor-pointer`}>
                  <option value="" disabled>—</option>
                  {t.contact.subjects.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-bronze block mb-2">{t.contact.message}</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="group inline-flex items-center gap-3 bg-terra text-paper hover:bg-terraBright disabled:opacity-50 transition-colors px-9 py-4 font-mono2 text-[11px] uppercase tracking-[0.2em]"
              >
                {sending ? t.contact.sending : t.contact.submit}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
